// Pure helpers for dialog-carry (Phase 1) — kept framework-free (no Lit, no DOM
// beyond the standard URL parser) so the load-bearing transport bounds and URL
// threading are unit-testable without rendering the <emma-chat> element. The
// element (emma-chat.ts handleDeployClick) is the only caller.

import type { TurnEntry } from '../state/index.js';
import { maxCardsForSurface } from '../util/constants.js';

// Mirror the BFF caps (transcript.go), but the binding constraint here is the
// browser's keepalive transport: a keepalive request body is capped at 64KB and
// an oversized body is dropped BEFORE it leaves, so the BFF's post-decode caps
// can't rescue it. Front-truncate under that ceiling, with envelope margin for
// the `{"key":"<uuid>","turns":...}` wrapper.
export const TRANSCRIPT_MAX_TURNS = 12;
export const TRANSCRIPT_MAX_BYTES = 60 * 1024;

/** Front-truncate the transcript to fit the keepalive transport: keep at most
 *  the newest TRANSCRIPT_MAX_TURNS turns, then drop oldest until the serialized
 *  `{key,turns}` body is under TRANSCRIPT_MAX_BYTES (mirrors the BFF's
 *  front-truncation, on the transport that actually enforces a limit). Returns
 *  [] when even the single newest turn exceeds the budget — the caller then
 *  skips the stash entirely (no nonce threaded, portal finds nothing). */
export function boundTranscript(all: TurnEntry[], nonce: string): TurnEntry[] {
  let turns =
    all.length > TRANSCRIPT_MAX_TURNS ? all.slice(-TRANSCRIPT_MAX_TURNS) : all.slice();
  const bodyLength = (list: TurnEntry[]): number =>
    JSON.stringify({ key: nonce, turns: list }).length;
  while (turns.length > 1 && bodyLength(turns) > TRANSCRIPT_MAX_BYTES) {
    turns = turns.slice(1);
  }
  return bodyLength(turns) > TRANSCRIPT_MAX_BYTES ? [] : turns;
}

/** Resolve the carried deploy config (a `configId`) to the `tXcY` id of the
 *  matching card in the hydrated turns, or null when it isn't present among the
 *  VISIBLE cards. Scans from the NEWEST turn back so that when the same config
 *  appears in several turns the pick resolves to the turn closest to the deploy
 *  click (usually the last, already-expanded turn). ti = index into `turns`,
 *  ci = index into that turn's result cards.
 *
 *  CRITICAL: <emma-chat-turn> renders only the first maxCardsForSurface(surface)
 *  cards and builds card ids from that SLICED index, so we only match within the
 *  same visible window FOR THE SAME SURFACE — pass the widget's `surface` so
 *  both sides share one cap (portal = all cards, standalone = 3). A config
 *  sitting beyond the cap (the BFF returns up to 5, and the best-price pick can
 *  be at index 3-4 on standalone) is NOT rendered, so we return null for it and
 *  let the portal fall back to startWithConfig (which fetches + appends a
 *  single visible, highlightable card). Matching the absolute index would point
 *  selectedCardId at a card that was never in the DOM → no highlight AND a
 *  skipped fetch. */
export function findSelectedCardId(
  turns: TurnEntry[],
  configId: string,
  surface: string,
): string | null {
  if (!configId || !Array.isArray(turns)) return null;
  const maxCards = maxCardsForSurface(surface);
  for (let ti = turns.length - 1; ti >= 0; ti--) {
    const cards = turns[ti]?.sseData?.result?.results;
    if (!cards) continue;
    const ci = cards
      .slice(0, maxCards)
      .findIndex((card) => card?.configId === configId);
    if (ci >= 0) return `t${ti}c${ci}`;
  }
  return null;
}

/** Thread the dialog-carry nonce INTO a pre-built signup URL by appending it to
 *  the URL's `redirect=` target (Keycloak preserves only the redirect target's
 *  own query across the auth round-trip — a top-level param would be dropped).
 *  Mirrors HandleAction's server-side append used on the postAction path.
 *  Returns the URL unchanged when there's no nonce, no redirect target, or the
 *  URL can't be parsed. */
export function threadTranscriptKey(signupUrl: string, nonce: string | undefined): string {
  if (!nonce) return signupUrl;
  try {
    const parsed = new URL(signupUrl);
    const target = parsed.searchParams.get('redirect');
    if (!target) return signupUrl;
    parsed.searchParams.set('redirect', `${target}&transcript=${nonce}`);
    return parsed.toString();
  } catch {
    return signupUrl;
  }
}
